import { Add, DeleteOutline } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { invoiceEditorInnerBorder, invoiceEditorSectionCardSx } from "../../../ui/invoiceEditorSectionSurface";
import type { InvoiceAdjustmentKind, InvoiceAppliesTo } from "../shared/types";
import type { InvoiceDiscountApplyToOption } from "./InvoiceDiscountsCard";

export type InvoiceServiceFeeDraft = {
  id: string;
  label: string;
  feeType?: string;
  kind: InvoiceAdjustmentKind;
  value: string;
  appliesToType: InvoiceAppliesTo["type"];
  appliesToKey?: string;
};

type Props = {
  items: InvoiceServiceFeeDraft[];
  applyToOptions: InvoiceDiscountApplyToOption[];
  computedAmounts: Record<string, number>;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onChange: (id: string, patch: Partial<InvoiceServiceFeeDraft>) => void;
  totalAmount: number;
  showValidation?: boolean;
};

function formatCurrency(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

export function InvoiceServiceFeesCard({
  items,
  applyToOptions,
  computedAmounts,
  onAdd,
  onRemove,
  onChange,
  totalAmount,
  showValidation = false,
}: Props) {
  return (
    <Card elevation={0} sx={invoiceEditorSectionCardSx}>
      <Box sx={{ p: 2, borderBottom: invoiceEditorInnerBorder }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
          Service fees
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem", mt: 0.5 }}>
          Add one or more service fees as a fixed amount or a percentage. Percentage fees apply after discounts, on the remaining amount of the whole
          invoice, a specific service, or an extra service.
        </Typography>
      </Box>

      <Stack spacing={0} divider={<Divider flexItem />}>
        {items.map((item) => {
          const parsedValue = Number.parseFloat(String(item.value || "0"));
          const hasValue = Number.isFinite(parsedValue) && parsedValue >= 0;
          const hasLabel = item.label.trim().length > 0;
          const labelError = showValidation && !hasLabel;
          const valueError = showValidation && !hasValue;

          return (
            <Box
              key={item.id}
              sx={{
                p: 2,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 220px auto" },
                gap: 1.25,
                alignItems: "start",
              }}
            >
              {/* Row 1: Label & ToggleButtonGroup */}
              <TextField
                size="small"
                label="Label"
                value={item.label}
                onChange={(e) => onChange(item.id, { label: e.target.value })}
                placeholder="e.g. Payment fee"
                fullWidth
                error={labelError}
                helperText={labelError ? "Label is required." : undefined}
                sx={{ gridColumn: { xs: "1", sm: "1" } }}
              />
              <Box sx={{ gridColumn: { xs: "1", sm: "2" }, display: "flex", alignItems: "center" }}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={item.kind}
                  onChange={(_, value: InvoiceAdjustmentKind | null) => {
                    if (value) onChange(item.id, { kind: value });
                  }}
                  sx={{
                    "& .MuiToggleButton-root": { px: 1.5, py: 0.5, fontWeight: 700 },
                  }}
                >
                  <ToggleButton value="amount">$</ToggleButton>
                  <ToggleButton value="percentage">%</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {/* Row 2: Applies to, Amount TextField & Remove IconButton */}
              <TextField
                size="small"
                select
                label="Applies to"
                value={item.appliesToKey || "invoice"}
                onChange={(e) => {
                  const option = applyToOptions.find((opt) => opt.key === e.target.value);
                  onChange(item.id, {
                    appliesToKey: e.target.value,
                    appliesToType: option?.type ?? "invoice",
                  });
                }}
                fullWidth
                sx={{ gridColumn: { xs: "1", sm: "1" } }}
              >
                {applyToOptions.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label={item.kind === "percentage" ? "Percent" : "Amount"}
                type="number"
                inputProps={{ min: 0, step: "0.01", max: item.kind === "percentage" ? 100 : undefined }}
                value={item.value}
                onChange={(e) => onChange(item.id, { value: e.target.value })}
                error={valueError}
                helperText={valueError ? "Enter a valid amount." : `Fee: ${formatCurrency(computedAmounts[item.id] ?? 0)}`}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{item.kind === "percentage" ? "%" : "$"}</InputAdornment>,
                }}
                fullWidth
                sx={{ gridColumn: { xs: "1", sm: "2" } }}
              />
              <IconButton
                aria-label="Remove service fee"
                onClick={() => onRemove(item.id)}
                size="small"
                sx={{
                  gridColumn: { xs: "1", sm: "3" },
                  gridRow: { sm: "2" },
                  justifySelf: { xs: "end", sm: "center" },
                  alignSelf: "start",
                  mt: 0.5,
                }}
              >
                <DeleteOutline />
              </IconButton>
            </Box>
          );
        })}
      </Stack>

      <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        <Button startIcon={<Add />} onClick={onAdd} sx={{ textTransform: "none", fontWeight: 700 }}>
          Add service fee
        </Button>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Service fees total {formatCurrency(totalAmount)}
        </Typography>
      </Box>
    </Card>
  );
}
